import {useCallback, useEffect, useRef} from 'react'
import { getXtractTableState, saveXtractTableState, columnNameAsString } from '../../XtractTableUtils.jsx'


const useTableState = ({
  table,
  enableMultiSort,
  columns,
  userStateKey,
  patientId,
  columnsCompatibleWithV7,
  defaultSettings,
  onInitComplete,
  pagination,
  pageSizeOptions,
  showFilters,
  setShowFilters,
  setColumnFilters,
  setPagination,
  // When true, column visibility always follows the column `show` defaults on
  // every open, ignoring any per-user selection persisted under userStateKey.
  // Other persisted state (filters, sorting, sizing, page size) is still
  // restored. Used by config-driven tables like the mix queue.
  resetColumnVisibilityToDefaults = false
}) => {

  useEffect(() => {
    // restore our state from the store (if we have a key name to use)
    const storedState = getXtractTableState(userStateKey, patientId)
    restoreState(storedState, true)

    // when we unmount, save our settings to the store (if we have a key name to use)
    return () => {
      const tableState = marshalSettingsRef.current()
      saveXtractTableState(userStateKey, tableState, patientId)
    }
  }, [])

  const restoreState = useCallback((storedState, init=false) => {
    // if the column IDs look like numbers, then this is old-style and needs special handling
    // (skipped entirely when the table always reverts column visibility to `show`
    // defaults, so the else branch below rebuilds it from the current config)
    if (!resetColumnVisibilityToDefaults && storedState.selectedColumns && storedState.selectedColumns.length > 0) {
      const id = storedState.selectedColumns[0].id
      if (!isNaN(parseFloat(id)) && isFinite(id)) {
        if (!columnsCompatibleWithV7) {
          storedState = defaultSettings
        } else {
          storedState.selectedColumns =
            table?.getAllLeafColumns().length
              ? storedState.selectedColumns.map(column => {
                return {
                  ...column,
                  id: table?.getAllLeafColumns()[column.id].id,
                  show: true
                }
              })
              : []
        }
      }
    } else {
      // default to 'show all columns that are marked as such'

      storedState.selectedColumns =
      table?.getAllLeafColumns().length
        ? columns
          .map((column) => {
            return {
              id: column.id,
              name: columnNameAsString(column),
              show:
                (column.showable === undefined || column.showable) &&
                (column.show === undefined || column.show)
            }
          })
          .filter(column => column.show)
        : []
    }

    // if the column IDs look like numbers, then this is old-style and needs special handling
    if (storedState.sorted && storedState.sorted.length > 0) {
      const id = storedState.sorted[0].id
      if (!isNaN(parseFloat(id)) && isFinite(id)) {
        if (!columnsCompatibleWithV7) {
          storedState = defaultSettings
        } else {
          storedState.sorted =
          table?.getAllLeafColumns().length
            ? storedState.sorted.map(column => {
              return {
                ...column,
                id: table?.getAllLeafColumns()[column.id].id
              }
            })
            : []
        }
      }
    }

    // if the state has 'resized' element, it is old-style - convert
    if (storedState.resized) {
      storedState.columnSizing = {}
      storedState.resized.forEach(columnSize => {
        storedState.columnSizing[columnSize.id] = columnSize.value
      })
      delete storedState.resized
    }

    if (storedState.selectedColumns) {
      table?.getAllLeafColumns().forEach(column => {
        const v = storedState.selectedColumns.find(col => {
          return col.id === column.id
        })
        column.toggleVisibility(!!v?.show)
      })
    }

    if (storedState.sorted) {
      table?.getAllLeafColumns().forEach(column => {
        column.clearSorting()
        const v = storedState.sorted.find(col => {
          return col.id === column.id
        })
        if (v) {
          column.toggleSorting(v.desc, enableMultiSort !== false)
        }
      })
    }

    table?.getAllLeafColumns().forEach(column => {
      const v = storedState.selectedColumns.some(col => { return col.id === column.id })
      column.toggleVisibility(v)
    })

    if(storedState) {
      const pageSize = storedState?.pageSize && pageSizeOptions.includes(storedState.pageSize) ? storedState.pageSize : pagination.pageSize
      const newPagination = {
        pageSize,
        pageIndex: 0
      }

      setPagination(newPagination)
      if(storedState.columnSizing) {
        table.setColumnSizing(storedState.columnSizing)
      }
      if(storedState.filtered) {
        setColumnFilters(storedState.filtered)
      }
      if(storedState.showFilters) {
        setShowFilters(storedState.showFilters)
      }
    }

    if(init) {
      onInitComplete(storedState)
    }

  }, [columns, defaultSettings, columnsCompatibleWithV7, enableMultiSort, onInitComplete, table, pagination.pageSize, pageSizeOptions, setShowFilters, resetColumnVisibilityToDefaults])

  // build an object that contains the salient table settings
  const marshalSettings = useCallback(() => {
    const selectedColumns = table?.getAllLeafColumns()
      .filter(column => column.getIsVisible())
      .map(column => {
        return ({
          id: column.id,
          name: columnNameAsString(column.columnDef),
          show: true
        })
      })

    const tableState = table?.getState()
    const settings = {
      showFilters,
      selectedColumns,
      sorted: tableState.sorting,
      page: tableState.pagination.pageIndex,
      pageSize: tableState.pagination.pageSize,
      columnSizing: tableState.columnSizing,
      filtered: tableState.columnFilters
    }

    return settings
  }, [
    table?.getState,
    table?.getAllLeafColumns,
    showFilters
  ])

  // this ref is necessary in order this to be called correctly from
  // the useEffect unmount
  const marshalSettingsRef = useRef(marshalSettings)
  useEffect(() => {
    marshalSettingsRef.current = marshalSettings
  },[marshalSettings])

  return {
    marshalSettings,
    restoreState
  }
}

export default useTableState